import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { useApi } from 'services/api';
import useStore from 'services/store';
import { PAGES } from 'consts/pages';
import { toast } from 'utils/toast';
import { extractErrorMessage } from 'utils/api';
import type { components } from 'types/schemas/api-schema';

type UserViewPayload = components['schemas']['UserViewAttributes'];
type WidgetConfigItem = NonNullable<UserViewPayload['widgets']>[number];
export type SavedLogView = components['schemas']['UserViewResponse'];
type ViewsResponse = components['schemas']['PagedUserViews'];

type StoreState = {
  dashboardConfiguration: {
    config: {
      presets: Record<string, Record<string, WidgetConfigItem[]>>;
      timechartBreakdown?: Record<string, string | null>;
    };
    selectedPreset: Record<string, string>;
  };
};

const QUERY_KEY = ['users', 'current', 'views', 'logsView'] as const;
const LOG_VIEW_LABEL = 'logsView';
const DEFAULT_PROFILE_TYPES: NonNullable<UserViewPayload['profileTypes']> = ['sensor', 'zeek'];
const DEFAULT_SEARCH_TYPE: UserViewPayload['searchType'] = 'O_SENSOR';

export default function useSavedLogViews() {
  const api = useApi();
  const queryClient = useQueryClient();
  const page = PAGES.investigator;

  const pagePresets = useStore((state: StoreState) => state.dashboardConfiguration.config.presets[page]) as
    | Record<string, WidgetConfigItem[]>
    | undefined;

  const selectedPreset = useStore((state: StoreState) => state.dashboardConfiguration.selectedPreset[page]) as string;

  const list = useQuery<ViewsResponse, unknown, { items: SavedLogView[] }>({
    queryKey: QUERY_KEY,
    queryFn: () => api.get<unknown, ViewsResponse>('/users/current/views'),
    retry: false,
    staleTime: 30_000,
    select: (data) => {
      if (!data || !data.items) {
        return { items: [] };
      }
      return {
        items: data.items.filter(
          (x): x is SavedLogView => Array.isArray(x.labels) && x.labels.includes(LOG_VIEW_LABEL),
        ),
      };
    },
  });

  const currentBreakdown = useStore(
    (state: StoreState) => state.dashboardConfiguration.config.timechartBreakdown?.[page],
  ) as string | null | undefined;

  const create = useMutation({
    mutationFn: (name: string) => {
      const widgets = pagePresets?.[selectedPreset] || [];

      const labels = [LOG_VIEW_LABEL];
      if (currentBreakdown) {
        labels.push(`breakdown:${currentBreakdown}`);
      }

      const payload: UserViewPayload = {
        name,
        description: '',
        labels,
        profileTypes: DEFAULT_PROFILE_TYPES,
        searchType: DEFAULT_SEARCH_TYPE,
        widgets,
      };

      return api.post('/users/current/views', payload);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (error: AxiosError) => {
      const errorMessage = extractErrorMessage(error) || 'Failed to save view';
      toast({
        status: 'error',
        content: errorMessage,
        duration: 2000,
      });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/users/current/views/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const upsert = useMutation({
    mutationFn: (view: SavedLogView) => {
      const baseLabels = (view.labels || []).filter((label) => !label.startsWith('breakdown:'));
      const labels = baseLabels.length > 0 ? baseLabels : [LOG_VIEW_LABEL];
      if (currentBreakdown) {
        labels.push(`breakdown:${currentBreakdown}`);
      }

      const payload: UserViewPayload = {
        name: view.name ?? '',
        description: view.description || '',
        labels,
        profileTypes: view.profileTypes || DEFAULT_PROFILE_TYPES,
        searchType: view.searchType || DEFAULT_SEARCH_TYPE,
        widgets: view.widgets || [],
      };

      return api.post(`/users/current/views/${view.id}`, payload);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
    onError: (error: AxiosError) => {
      const errorMessage = extractErrorMessage(error) || 'Failed to update view';
      toast({
        status: 'error',
        content: errorMessage,
        duration: 2000,
      });
    },
  });

  const findByName = useCallback(
    (name: string) => list.data?.items?.find((x: SavedLogView) => x.name === name),
    [list.data?.items],
  );

  return { list, create, remove, upsert, findByName };
}
